import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import NameInput from "../components/NameInput";
import axios from "axios";
import DatePickerField from "../components/DatePickerField";
import AmountDropdown from "../components/AmountDropdown";
import EmailInputField from "../components/EmailInputField";
import AddressInput from "../components/AddressField";
import LBAConfirmation from "../components/LBAConfirmation";
import PortalPopup from "../components/PortalPopup";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { openBankingService } from "../services/openBankingService";
import {
  Pen,
  Upload,
  Globe,
  Mail,
  CheckCircle,
  AlertTriangle,
  FileText,
} from "lucide-react";

const LbaForm = () => {
  const location = useLocation();

  const [name, setName] = useState("");
  const [autoIncrementAccount, setautoIncrementAccount] = useState("");
  const [userData, setUserData] = useState({ name: "" });
  const [accountNumber, setAccountNumber] = useState("");
  const [dueDate, setDueDate] = useState(null);
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("£");
  const [postcode, setPostcode] = useState("");
  const [houseName, setHouseName] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [isDarkMode] = useState(false);
  const [country, setCountry] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [email, setEmail] = useState("");
  const [isLBAConfirmationOpen, setLBAConfirmationOpen] = useState(false);
  const [isFromXero, setIsFromXero] = useState(false);
  const [savedLbaId, setSavedLbaId] = useState(null);
  const [signatureImage, setSignatureImage] = useState(null);
  const fileInputRef = useRef(null);
  const API_URL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("authToken");
  const user = JSON.parse(localStorage.getItem("authUser"));
  const [openBankingStatus, setOpenBankingStatus] = useState(null);

  const [isSaving, setIsSaving] = useState(false);

  const currencies = [
    { code: "GBP", name: "British Pound", symbol: "£" },
    { code: "EUR", name: "Euro", symbol: "€" },
    { code: "INR", name: "Indian Rupee", symbol: "₹" },
    { code: "CAD", name: "Canadian Dollar", symbol: "$" },
    { code: "AUD", name: "Australian Dollar", symbol: "$" },
    { code: "USD", name: "US Dollar", symbol: "$" },
  ];

  const handleDateChange = (d) => setDueDate(d);
  const handleAmountChange = (v) => setAmount(v);
  const handleCurrencyChange = (s) => setCurrency(s);
  const handleEmailChange = (e) => setEmail(e.target.value);

  const handleButtonClick = () => fileInputRef.current.click();
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (autoIncrementAccount || hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchNextReference = async () => {
      try {
        const res = await fetch(`${API_URL}/api/lba/next-reference`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.success) setautoIncrementAccount(data.reference);
      } catch (err) {
        console.error("Failed to fetch next reference:", err);
      }
    };
    

    fetchNextReference();
  }, []);

  useEffect(() => {
  openBankingService
    .getStatus()
    .then(setOpenBankingStatus)
    .catch((err) => console.error("Failed to load Open Banking status:", err));
}, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === "image/jpeg" || file.type === "image/png")) {
      const reader = new FileReader();
      reader.onload = (ev) => setSignatureImage(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      alert("Please select a valid image file (JPEG or PNG)");
    }
  };

  const handleAddressSelect = (addressComponents, formattedAddress) => {
    const getComponent = (type, shortName = false) => {
      const c = addressComponents.find((comp) => comp.types.includes(type));
      return c ? (shortName ? c.short_name : c.long_name) : "";
    };
    const streetNumber = getComponent("street_number");
    const route = getComponent("route");
    const locality = getComponent("locality");
    const postalTown = getComponent("postal_town");
    const administrativeArea = getComponent("administrative_area_level_2");
    const country = getComponent("country");
    const postalCode = getComponent("postal_code");
    let buildingName = streetNumber;
    if (formattedAddress && !streetNumber && formattedAddress.includes(",")) {
      buildingName = formattedAddress.split(",")[0].trim();
    }
    setPostcode(postalCode);
    setHouseName(buildingName || "");
    setStreet(route || "");
    setCity(postalTown || locality || administrativeArea || "");
    setCountry(country || "United Kingdom");
  };

  useEffect(() => {
    if (!location.state) return;
    setUserData(location.state);
    const s = location.state;

    if (s.name) setName(s.name);
    if (s.businessName) setBusinessName(s.businessName);
    if (s.invoiceNumber) setInvoiceNumber(s.invoiceNumber);
    if (s.invoiceId) setInvoiceId(s.invoiceId);
    if (s.amount) setAmount(s.amount.toString());
    if (s.currency) setCurrency(s.currency);
    if (s.email) setEmail(s.email);
    if (s.houseName) setHouseName(s.houseName);
    if (s.street) setStreet(s.street);
    if (s.city) setCity(s.city);
    if (s.country) setCountry(s.country);
    if (s.postcode) setPostcode(s.postcode);
    if (s.accountNumber) setAccountNumber(s.accountNumber);
    if (s.dueDate) {
      setDueDate(
        typeof s.dueDate === "string" ? new Date(s.dueDate) : s.dueDate,
      );
    }
    // Any synced source (xero, keap, quickbooks, sage, business central…)
    // arrives with its data already in state; only a state with no source
    // at all falls back to looking the invoice up by id.
    if (s.source === "manual") {
      setIsFromXero(false);
    } else if (s.source) {
      setIsFromXero(true);
    } else {
      if (s.id) fetchInvoice(s.id);
    }
  }, [location]);

  const fetchInvoice = async (userId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/documents/get-invoice/${userId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      if (data?.invoiceNumbers) {
        setInvoiceNumber(data.invoiceNumbers);
        setInvoiceId(data.invoiceId);
      } else setInvoiceNumber("Not found");
    } catch (error) {
      console.error("Fetch Error:", error);
    }
  };

  const getFormattedDates = () => {
    const today = new Date();
    const formattedToday = today.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + 15);
    const formattedFutureDate = futureDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    return { formattedToday, formattedFutureDate };
  };

  const { formattedToday, formattedFutureDate } = getFormattedDates();

  const handleUpdateDetails = async (fd) => {
    const authenticatedUserId = user?.id;
    const updatedData = {
      userId: authenticatedUserId || location.state?.id || "",
      companyId: location.state?.companyId || user?.companyId || "",
      name: fd.name || "",
      autoIncrementAccount: autoIncrementAccount || "",
      businessName: fd.businessName || "",
      invoiceNumber: fd.invoiceNumber || "",
      invoiceId: fd.invoiceId || "",
      accountNumber: fd.accountNumber || "",
      dueDate: fd.dueDate || "",
      amount: fd.amount || "",
      email: fd.email || "",
      currency: fd.currency || "",
      status: "Pending",
      address: {
        house: fd.houseName || "",
        street: fd.street || "",
        city: fd.city || "",
        country: fd.country || "",
        postalCode: fd.postcode || "",
      },
      source: isFromXero ? "xero" : "manual",
    };
    try {
      const response = await fetch(`${API_URL}/api/lba/save_lba_form`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedData),
      });
      if (response.ok) {
        const responseData = await response.json();
        const lbaId =
          responseData.lbaEntry?.id || responseData.lbaId || responseData._id;

        const newAutoIncrement = responseData.lbaEntry?.autoIncrementAccount;
        if (newAutoIncrement) setautoIncrementAccount(newAutoIncrement);

        if (lbaId) {
          setSavedLbaId(lbaId);
          return lbaId;
        } else {
          alert("Error: Pre-LBA was saved but no ID was returned.");
          return null;
        }
      } else {
        throw new Error(await response.text());
      }
    } catch (error) {
      console.error("Error updating details:", error);
    }
  };

  const openLBAConfirmation = useCallback(async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const lbaId = await handleUpdateDetails({
        name,
        businessName,
        invoiceNumber,
        invoiceId,
        accountNumber,
        email,
        dueDate,
        amount,
        currency,
        houseName,
        street,
        city,
        country,
        postcode,
        signatureImage,
      });
      if (!lbaId) return;
      setSavedLbaId(lbaId);
      setLBAConfirmationOpen(true);
    } finally {
      setIsSaving(false);
    }
  }, [
    isSaving,
    name,
    businessName,
    invoiceNumber,
    invoiceId,
    accountNumber,
    email,
    dueDate,
    amount,
    currency,
    houseName,
    street,
    city,
    country,
    postcode,
    signatureImage,
  ]);

  const closeLBAConfirmation = useCallback(
    () => setLBAConfirmationOpen(false),
    [],
  );

  const addressParts = [houseName, street, city, country, postcode].filter(
    Boolean,
  );

  const PlaceholderBox = () => (
    <span
      style={{
        display: "inline-block",
        width: 120,
        height: 28,
        background: "#F1F5F9",
        borderRadius: 6,
        border: "1px solid #E2E8F0",
        verticalAlign: "middle",
      }}
    />
  );

  const primaryColor = "#0d6377";
  const consequenceColor = "#c45c00";
  const mutedColor = "#64748B";
  const neutralColor = "#1E293B";
  const offWhiteColor = "#F8FAFC";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=DM+Mono:wght@400;500&display=swap');

        .lbaf-root * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }

        .lbaf-form-panel {
          display: flex; flex-direction: column; gap: 18px; width: 400px; flex-shrink: 0;
        }

        .lbaf-section-title {
          font-size: 11px; font-weight: 700; color: #94A3B8;
          text-transform: uppercase; letter-spacing: 0.07em;
          margin: 8px 0 2px 0;
        }

        .lbaf-amount-label {
          font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 6px;
        }

        .lbaf-continue-btn {
          width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          padding: 11px 20px; border: none; border-radius: 10px;
          font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 700; color: #ffffff;
          background: linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%);
          box-shadow: 0 2px 8px rgba(37,99,235,0.3); cursor: pointer;
          transition: opacity 0.15s, transform 0.15s; margin-top: 8px;
        }
        .lbaf-continue-btn:hover { opacity: 0.9; transform: translateY(-1px); }

        .lbaf-sig-box {
          width: 180px; min-height: 68px; border: 1.5px dashed #CBD5E1; border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          background: #F8FAFC; cursor: pointer; padding: 12px;
          transition: border-color 0.15s;
        }
        .lbaf-sig-box:hover { border-color: #2563EB; }

        /* ── Preview panel ── */
        .lbaf-preview {
          flex: 1; background: #ffffff; border: 1px solid #E2E8F0; border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.04);
          padding: 32px; display: flex; flex-direction: column; gap: 16px;
          font-size: 13px; color: #1E293B; line-height: 1.75;
          max-width: 700px;
        }

        .lbaf-preview-header {
          display: flex; align-items: center; justify-content: space-between;
          padding-bottom: 16px;
        }

        .lbaf-ref-box {
          background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 10px;
          padding: 12px 16px; text-align: center;
        }

        .lbaf-ref-label { font-size: 11px; color: #94A3B8; margin-bottom: 4px; }
        .lbaf-ref-value { font-family: 'DM Mono', monospace; font-size: 20px; font-weight: 500; color: #0F172A; }

        /* ── FINAL PRE-LEGAL NOTICE header ── */
        .lbaf-notice-header {
          font-size: 18px; font-weight: 700; color: ${consequenceColor};
          letter-spacing: 0.02em;
        }
        .lbaf-notice-sub {
          font-size: 11px; font-weight: 700; color: ${neutralColor};
          margin-top: -4px;
        }

        /* ── Balance Panel ── */
        .lbaf-balance-panel {
          background: ${offWhiteColor};
          border: 1px solid #E2E8F0;
          border-radius: 8px;
          padding: 14px 16px;
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 8px;
        }
        .lbaf-balance-item {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .lbaf-balance-label {
          font-size: 8.5px;
          font-weight: 600;
          color: ${mutedColor};
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .lbaf-balance-value {
          font-size: 14px;
          font-weight: 700;
          color: ${neutralColor};
        }
        .lbaf-balance-value.lbaf-amount {
          font-family: 'DM Mono', monospace;
          font-size: 16px;
          color: ${consequenceColor};
        }
        .lbaf-balance-sub {
          font-size: 8.5px;
          color: ${mutedColor};
          font-weight: 400;
        }

        /* ── Checklist columns ── */
        .lbaf-checklist-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin: 4px 0 8px;
        }
        .lbaf-checklist-col {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .lbaf-checklist-header {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.03em;
          padding-bottom: 6px;
          border-bottom: 2px solid currentColor;
        }
        .lbaf-checklist-header.lbaf-why { color: ${primaryColor}; }
        .lbaf-checklist-header.lbaf-consequence { color: ${consequenceColor}; }
        .lbaf-checklist-item {
          display: flex;
          align-items: flex-start;
          gap: 6px;
          font-size: 11.5px;
          color: ${neutralColor};
          line-height: 1.4;
          padding: 2px 0;
        }
        .lbaf-checklist-item .check {
          color: ${primaryColor};
          font-weight: 700;
          flex-shrink: 0;
          margin-top: 1px;
        }
        .lbaf-checklist-item .check.consequence-check {
          color: ${consequenceColor};
        }
        .lbaf-checklist-sub {
          font-size: 9px;
          color: ${mutedColor};
          font-weight: 400;
          margin-bottom: 4px;
        }

        /* ── Urgency callout ── */
        .lbaf-urgency-box {
          background: #FFF6EA;
          border: 1px solid ${consequenceColor};
          border-left: 4px solid ${consequenceColor};
          border-radius: 4px;
          padding: 14px 16px;
          margin: 4px 0 8px;
        }
        .lbaf-urgency-header {
          font-size: 9.5px;
          font-weight: 700;
          color: ${consequenceColor};
          letter-spacing: 0.05em;
          margin-bottom: 4px;
        }
        .lbaf-urgency-text {
          font-size: 11.5px;
          color: ${neutralColor};
          line-height: 1.6;
        }
        .lbaf-urgency-text strong {
          color: ${consequenceColor};
        }
        .lbaf-urgency-contact {
          font-size: 10px;
          font-weight: 700;
          color: ${consequenceColor};
          margin-top: 6px;
        }

        /* ── Digital Case Intelligence ── */
        .lbaf-section-label {
          font-size: 10px;
          font-weight: 700;
          color: ${primaryColor};
          letter-spacing: 0.03em;
          text-transform: uppercase;
        }
        .lbaf-section-body {
          font-size: 11.5px;
          color: ${mutedColor};
          line-height: 1.6;
        }

        /* ── Evidence Status ── */
        .lbaf-evidence-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2px 16px;
          margin-top: 4px;
        }
        .lbaf-evidence-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          color: ${neutralColor};
          padding: 2px 0;
        }
        .lbaf-evidence-item .check {
          color: ${primaryColor};
          font-weight: 700;
        }

        /* ── Footer ── */
        .lbaf-footer-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          padding-top: 12px;
          border-top: 1px solid #E2E8F0;
          margin-top: 4px;
        }
        .lbaf-footer-left {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .lbaf-footer-left .line {
          font-size: 10px;
          color: #374151;
        }
        .lbaf-footer-left .link {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 10px;
          color: #2563EB;
        }
        .lbaf-footer-left .link a {
          color: #2563EB;
          text-decoration: underline;
        }
        .lbaf-footer-right .badge {
          font-size: 10px;
          font-weight: 700;
          color: ${primaryColor};
          letter-spacing: 0.02em;
        }
        .lbaf-footer-right .desc {
          font-size: 9.5px;
          color: ${mutedColor};
          line-height: 1.5;
          margin-top: 2px;
        }

        .lbaf-legal-notice {
          font-size: 9px;
          color: ${mutedColor};
          line-height: 1.6;
          padding-top: 10px;
          border-top: 1px solid #E2E8F0;
          margin-top: 4px;
        }
        .lbaf-legal-notice strong {
          color: ${neutralColor};
        }

        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div
        className="lbaf-root"
        style={{ display: "flex", alignItems: "flex-start", gap: 32 }}
      >
        {/* ── Left: Form ── */}
        <div className="lbaf-form-panel">
          <p className="lbaf-section-title">Recipient</p>
          <NameInput
            title="Full name"
            placeholder="John Clark"
            width="400px"
            value={name}
            onChange={(e) => setName(e.target.value)}
            isDarkMode={isDarkMode}
          />
          <NameInput
            title="Business name"
            placeholder="Enter business name"
            width="400px"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            isDarkMode={isDarkMode}
          />
          <NameInput
            title="Invoice number"
            placeholder="INV-0001"
            width="400px"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            isDarkMode={isDarkMode}
          />
          <EmailInputField
            title="Email"
            placeholder="name@yourcompany.com"
            value={email}
            onChange={handleEmailChange}
            validationMessage="Please provide a valid email address."
            iconColor="#EF4444"
            iconSize="16px"
            bgColorLight="#ffffff"
            bgColorDark="#0C111D"
            isDarkMode={isDarkMode}
          />

          <p className="lbaf-section-title" style={{ marginTop: 4 }}>
            Invoice details
          </p>
          <DatePickerField
            selectedDate={dueDate}
            value={dueDate}
            onChange={handleDateChange}
          />
          <div>
            <div className="lbaf-amount-label">
              Amount <span style={{ color: "#EF4444" }}>*</span>
            </div>
            <AmountDropdown
              currencies={currencies}
              onCurrencyChange={handleCurrencyChange}
              onAmountChange={handleAmountChange}
              initialCurrency={currency}
              initialAmount={amount}
            />
          </div>

          <p className="lbaf-section-title" style={{ marginTop: 4 }}>
            Address
          </p>
          <AddressAutocomplete
            label="Postcode"
            placeholder="Enter your postcode"
            onAddressSelect={handleAddressSelect}
            value={postcode}
          />
          <AddressInput
            label="House / Building name"
            placeholder="Enter your house / building name"
            value={houseName}
            onChange={(e) => setHouseName(e.target.value)}
          />
          <AddressInput
            label="Street"
            placeholder="Enter your street"
            value={street}
            onChange={(e) => setStreet(e.target.value)}
          />
          <AddressInput
            label="Town / City"
            placeholder="Enter your town / city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
          <AddressInput
            label="Country"
            placeholder="Enter your country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          />

          <button
            className="lbaf-continue-btn"
            onClick={openLBAConfirmation}
            disabled={isSaving}
          >
            {isSaving ? "Saving…" : "Continue →"}
          </button>
        </div>

        {/* ── Right: Letter preview ── */}
        <div className="lbaf-preview">
          {/* Letterhead */}
          <div className="lbaf-preview-header">
            <img
              src="assets/logo-horizontal-lba-form.png"
              style={{ height: 32, width: "auto" }}
              alt="6ELA"
            />
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontSize: 8,
                  letterSpacing: "0.05em",
                  color: "#94A3B8",
                  fontWeight: 600,
                }}
              >
                REFERENCE
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#0F172A" }}>
                {autoIncrementAccount || "—"}
              </div>
            </div>
          </div>

          <div style={{ borderTop: "1px solid #E2E8F0", margin: "0 0 12px" }} />

          {/* Meta row: DATE, RECIPIENT, BUSINESS */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 12,
              marginBottom: addressParts.length > 0 ? 8 : 12,
            }}
          >
            {[
              { label: "DATE", value: formattedToday },
              { label: "RECIPIENT", value: name || "—" },
              { label: "BUSINESS", value: businessName || "—" },
            ].map((col) => (
              <div key={col.label}>
                <div
                  style={{
                    fontSize: 8,
                    letterSpacing: "0.05em",
                    color: "#94A3B8",
                    fontWeight: 600,
                    marginBottom: 2,
                  }}
                >
                  {col.label}
                </div>
                <div
                  style={{ fontSize: 12, fontWeight: 700, color: "#0F172A" }}
                >
                  {col.value}
                </div>
              </div>
            ))}
          </div>

          {/* Address */}
          {addressParts.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  fontSize: 8,
                  letterSpacing: "0.05em",
                  color: "#94A3B8",
                  fontWeight: 600,
                  marginBottom: 2,
                }}
              >
                ADDRESS
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#0F172A" }}>
                {addressParts.join(", ")}
              </div>
            </div>
          )}

          {/* ── FINAL PRE-LEGAL NOTICE ── */}
          <div className="lbaf-notice-header">FINAL PRE-LEGAL NOTICE</div>
          <div className="lbaf-notice-sub">
            Action Required Before Legal Proceedings Are Considered
          </div>

          {/* ── Balance Panel ── */}
          <div className="lbaf-balance-panel">
            <div className="lbaf-balance-item">
              <span className="lbaf-balance-label">Outstanding Balance</span>
              <span className="lbaf-balance-value lbaf-amount">
                {amount ? `${currency} ${amount}` : "—"}
              </span>
              <span className="lbaf-balance-sub">
                Invoice(s): {invoiceNumber || "N/A"}
              </span>
            </div>
            <div className="lbaf-balance-item">
              <span className="lbaf-balance-label">Payment Due</span>
              <span className="lbaf-balance-value">
                {dueDate ? dueDate.toLocaleDateString("en-GB") : "N/A"}
              </span>
            </div>
            <div className="lbaf-balance-item">
              <span className="lbaf-balance-label">Status</span>
              <span
                className="lbaf-balance-value"
                style={{ color: consequenceColor }}
              >
                Final Opportunity
              </span>
              <span className="lbaf-balance-sub">Before Legal Escalation</span>
            </div>
          </div>

          {/* ── Checklist Grid ── */}
          <div className="lbaf-checklist-grid">
            {/* Left: Why Resolve This Now? */}
            <div className="lbaf-checklist-col">
              <div className="lbaf-checklist-header lbaf-why">
                WHY RESOLVE THIS NOW?
              </div>
              {[
                "Avoid legal proceedings",
                "Prevent additional costs and interest",
                "Protect your business credit profile",
                "Resolve the matter quickly and professionally",
              ].map((item) => (
                <div className="lbaf-checklist-item" key={item}>
                  <span className="check">✓</span>
                  {item}
                </div>
              ))}
            </div>

            {/* Right: Potential Consequences */}
            <div className="lbaf-checklist-col">
              <div className="lbaf-checklist-header lbaf-consequence">
                POTENTIAL CONSEQUENCES OF NON-PAYMENT
              </div>
              <div className="lbaf-checklist-sub">
                Failure to resolve this matter may result in:
              </div>
              {[
                "Formal legal proceedings",
                "Recovery of court fees",
                "Statutory interest",
                "Recoverable legal costs",
                "County Court Judgment (CCJ)",
                "Enforcement action following judgment",
              ].map((item) => (
                <div className="lbaf-checklist-item" key={item}>
                  <span className="check consequence-check">✓</span>
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* ── Urgency Callout ── */}
          <div className="lbaf-urgency-box">
            <div className="lbaf-urgency-header">⚠ ACTION REQUIRED</div>
            <div className="lbaf-urgency-text">
              Payment or a satisfactory response must be received no later than{" "}
              <strong>{formattedFutureDate}</strong>. Failure to respond within
              this period may result in the matter being escalated for legal
              review without further notice.
            </div>
            <div className="lbaf-urgency-contact">
              Reach us directly at hello@6ela.io to ensure prompt resolution
              before any further action.
            </div>
          </div>

          {/* ── Digital Case Intelligence ── */}
          <div>
            <div className="lbaf-section-label">Digital Case Intelligence</div>
            <div className="lbaf-section-body">
              This matter is being managed through the 6ELA platform. All
              communications, payment activity, debtor engagement and supporting
              evidence are automatically recorded to create a complete digital
              chronology of the matter.
            </div>
          </div>

          {/* ── Evidence Status ── */}
          <div>
            <div className="lbaf-section-label">Evidence Status</div>
            <div className="lbaf-evidence-grid">
              {[
                "Outstanding balance verified",
                "Invoice history recorded",
                "Communication history retained",
                "Payment history recorded",
                "Supporting documentation available",
              ].map((item) => (
                <div className="lbaf-evidence-item" key={item}>
                  <span className="check">✓</span>
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* ── Sign-off ── */}
          <div style={{ marginTop: 4 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#0F172A" }}>
              Kind regards,
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#0F172A" }}>
              6ELA Automated Credit Control Platform
            </div>
            <div
              style={{ fontSize: 10, fontStyle: "italic", color: "#64748B" }}
            >
              Issued on behalf of{" "}
              {businessName || user?.companyName || "{{CompanyName}}"}
            </div>
          </div>

          {/* ── Payout Account (Open Banking) — shown for both 'verified' and
     'unverified', matching the same check used in SettingsBillingPage.
     Stitch isn't live yet, so every saved account is currently
     'unverified' — this is deliberately shown anyway so the debtor still
     sees where to pay. NOTE: sendLbaEmail on the backend currently only
     swaps in bank details for 'verified' accounts (see
     OpenBankingService.getPayoutDetailsFromCompany, which requires
     openBankingStatus === "verified"), so until that's updated to match,
     the actual outgoing email will still use a Stripe/Paystack payment
     link even when this preview shows bank details. Worth updating that
     backend condition to include 'unverified' too, so the preview and
     the real email stay consistent while Stitch is offline. ── */}
          {["verified", "unverified"].includes(openBankingStatus?.status) && (
            <div>
              <div className="lbaf-section-label">Pay Your Outstanding Balance by EFT</div>
              <div
                style={{
                  background: offWhiteColor,
                  border: "1px solid #E2E8F0",
                  borderRadius: 8,
                  padding: "12px 16px",
                  marginTop: 6,
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "4px 16px",
                }}
              >
                {[
                  { label: "Bank", value: openBankingStatus.account?.bankName },
                  { label: "Account holder", value: openBankingStatus.account?.accountHolderName },
                  { label: "Account number", value: openBankingStatus.account?.accountNumberMasked },
                  { label: "Branch code", value: openBankingStatus.account?.branchCode },
                ]
                  .filter((row) => row.value)
                  .map((row) => (
                    <div key={row.label} style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5 }}>
                      <span style={{ color: mutedColor }}>{row.label}</span>
                      <span style={{ fontWeight: 700, color: neutralColor }}>{row.value}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* ── Footer ── */}
          <div className="lbaf-footer-grid">
            <div className="lbaf-footer-left">
              <div className="line">
                Issued via the 6ELA Accounts Receivable Platform
              </div>
              <div
                className="line"
                style={{ display: "flex", alignItems: "center", gap: 4 }}
              >
                Powered by{" "}
                <img
                  src="assets/logo-horizontal-lba-form.png"
                  style={{ height: 14, width: "auto" }}
                  alt="6ELA"
                />
              </div>
              <div className="link">
                <Globe size={12} />
                <a href="https://www.6ela.com" target="_blank" rel="noreferrer">
                  www.6ela.com
                </a>
              </div>
              <div className="link">
                <Mail size={12} />
                <a href="mailto:support@6ela.com">support@6ela.com</a>
              </div>
            </div>
            <div className="lbaf-footer-right">
              <div className="badge">Litigation Ready™ Case File</div>
              <div className="desc">
                Every action is securely logged into a chronological, auditable
                case file. Where instructed, this can be shared with legal
                professionals to support pre-action review.
              </div>
            </div>
          </div>

          {/* ── Important Legal Notice ── */}
          <div className="lbaf-legal-notice">
            <strong>Important Legal Notice</strong>
            <br />
            This letter forms part of the creditor's formal pre-action recovery
            process. Failure to respond may result in the matter being referred
            to appointed legal professionals for review. Any subsequent legal
            proceedings may rely upon the documented evidence maintained within
            the 6ELA platform, including communications, invoices, payment
            history and supporting documentation.
            <br />
            <br />
            Where proceedings are commenced, the creditor may seek recovery of
            the outstanding balance together with any applicable statutory
            interest, court fees and recoverable legal costs.
            <br />
            <br />
            This communication has been issued via the 6ELA Accounts Receivable
            Platform on behalf of the creditor and should be treated as an
            important legal communication requiring immediate attention.
          </div>
        </div>
      </div>

      {isLBAConfirmationOpen && (
        <PortalPopup
          overlayColor="rgba(113,113,113,0.3)"
          placement="Centered"
          onOutsideClick={closeLBAConfirmation}
        >
          <LBAConfirmation
            onClose={closeLBAConfirmation}
            formData={{
              name,
              businessName,
              invoiceNumber,
              invoiceId,
              accountNumber,
              email,
              dueDate,
              amount,
              currency,
              houseName,
              street,
              city,
              country,
              postcode,
              autoIncrementAccount,
              signatureImage,
              lbaId: savedLbaId,
              companyName: user?.companyName || "",
            }}
          />
        </PortalPopup>
      )}
    </>
  );
};

export default LbaForm;